import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useState, useCallback, useMemo } from 'react';
import * as RadixToggle from '@radix-ui/react-toggle-group';
import toggleChipGroupStyles from './ToggleChip.styles.esm.js';
import { ToggleChipItem } from './ToggleChipItem.esm.js';

const ToggleChip = forwardRef((props, ref) => {
    var _a;
    const { items, selectionType = "single", defaultSelectedIds = [], className, inverse = false, onChange, "aria-label": ariaLabel } = props, rest = __rest(props, ["items", "selectionType", "defaultSelectedIds", "className", "inverse", "onChange", "aria-label"]);
    const itemRefs = useRef([]);
    const isSingle = selectionType === "single";
    const roleToggleChip = isSingle ? "radio" : "checkbox";
    const labelLegend = ariaLabel || "Available options";
    const groupLabelId = useId();
    const defaultSelectedValue = isSingle
        ? [String(defaultSelectedIds[0])]
        : defaultSelectedIds.map(String);
    const [selected, setSelected] = useState(defaultSelectedValue);
    const { base } = toggleChipGroupStyles();
    const handleChange = useCallback((value) => {
        var _a;
        let selectedIds;
        if (isSingle) {
            selectedIds = typeof value === "string" ? value : ((_a = value[0]) !== null && _a !== void 0 ? _a : "");
            setSelected([selectedIds]);
        }
        else {
            selectedIds = Array.isArray(value) ? value : [value];
            setSelected(selectedIds);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(selectedIds);
    }, [isSingle, onChange]);
    const handleKeyDown = useCallback((e) => {
        var _a;
        if (isSingle && items && items.length > 0) {
            const currentIndex = items.findIndex((item) => item.id === selected[0]);
            let nextIndex = currentIndex;
            if (e.code === "Enter" ||
                e.key === "Enter" ||
                e.key === " " ||
                e.code === "Space") {
                e.preventDefault();
                return;
            }
            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                nextIndex = (currentIndex + 1) % items.length;
            }
            else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                nextIndex = (currentIndex - 1 + items.length) % items.length;
            }
            if (nextIndex !== currentIndex) {
                const nextId = String(items === null || items === void 0 ? void 0 : items[nextIndex].id);
                if (nextId !== selected[0]) {
                    handleChange === null || handleChange === void 0 ? void 0 : handleChange(nextId);
                    (_a = itemRefs.current[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                }
                e.preventDefault();
            }
        }
    }, [isSingle, items, selected, handleChange]);
    const groupProps = isSingle
        ? {
            type: "single",
            value: (_a = selected[0]) !== null && _a !== void 0 ? _a : "",
            onValueChange: handleChange,
        }
        : {
            type: "multiple",
            value: selected,
            onValueChange: handleChange,
        };
    const renderToggleChips = useMemo(() => items === null || items === void 0 ? void 0 : items.map(({ id, label }, index) => {
        const stringId = String(id);
        const isSelected = selected.includes(stringId);
        const tabIndex = isSingle ? (isSelected ? 0 : -1) : 0;
        return (jsx(ToggleChipItem, { ref: (element) => (itemRefs.current[index] = element), "data-testid": `toggle-chip-${stringId}`, tabIndex: tabIndex, isSelected: isSelected, value: stringId, label: label, inverse: inverse, role: roleToggleChip, onKeyDown: (isSingle && isSelected && handleKeyDown) || undefined }, `toggle-chip-${stringId}`));
    }), [handleKeyDown, inverse, isSingle, items, roleToggleChip, selected]);
    return isSingle ? (jsx(RadixToggle.Root, Object.assign({ ref: ref, role: "radiogroup", "aria-label": labelLegend, className: base({ class: className }) }, groupProps, rest, { children: renderToggleChips }))) : (jsx(RadixToggle.Root, Object.assign({ className: base({ class: className }), asChild: true }, groupProps, { children: jsxs("fieldset", Object.assign({ ref: ref, "aria-labelledby": groupLabelId, role: undefined, tabIndex: undefined }, rest, { children: [jsx("legend", { id: groupLabelId, className: "iris:sr-only", children: labelLegend }), renderToggleChips] })) })));
});
ToggleChip.displayName = "ToggleChip";

export { ToggleChip };
